import { Container, IDestroyOptions, Point, Rectangle } from "pixi.js";
import { WeaponType } from "../../../models/data/WeaponType";
import { Event } from "event-system";
import { Character } from "../../3d/character/Character";
import { CommonResourcesPackage } from "../../../../../common/resources/CommonResourcesPackage";
import { HBox } from "../../../../../common/components/HBox";
import { VBox } from "../../../../../common/components/VBox";
import { nameof } from "bindable-data";
import { WeaponButton } from "./WeaponButton";


type Orientation = "vertical" | "horizontal";

export class WeaponsPanel extends Container {
    public readonly onWeaponClick: Event<WeaponType>;
    private readonly weaponButtons: Map<WeaponType, WeaponButton>;
    private box: Container;

    public constructor(
        commonResources: CommonResourcesPackage,
        private readonly character: Character
    ) {
        super();
        this.eventMode = "static";
        this.box = this.addChild(new HBox(20));
        this.onWeaponClick = new Event();
        this.weaponButtons = new Map();
        for (const [type, count] of character.weapons) {
            const button = new WeaponButton(commonResources, type, count, character.currentWeapon.value === type);
            button.onUp.add(this.onWeaponClickHandler, this);
            this.weaponButtons.set(type, button);
            this.box.addChild(button);
        }
        this.character.propertyChanged.add(this.onCharacterPropertyChange, this);
        this.hitArea = new Rectangle(0, 0, this.width, this.height);
    }

    public getFlyTargetCoordinates(type: WeaponType): Point {
        const boosterButton = this.weaponButtons.get(type);
        return boosterButton.flyTargetCoordinates;
    }

    public changeOrientation(orientation: Orientation): void {
        let box: Container;
        switch (orientation) {
            case "horizontal":
                if (this.box instanceof HBox) {
                    return;
                }
                box = new HBox(20);
                break;
            case "vertical":
                if (this.box instanceof VBox) {
                    return;
                }
                box = new VBox(0);
                break;
        }

        for (const child of Object.assign([], this.box.children.reverse())) {
            child.position.set(0);
            box.addChild(child);
        }
        this.removeChild(this.box);
        this.box.destroy({ baseTexture: false, children: true, texture: false });
        this.box = this.addChild(box);
        this.updateHitArea();
    }

    private onWeaponClickHandler(button: WeaponButton): void {
        this.onWeaponClick.emit(button.type);
    }

    public onCharacterPropertyChange(character: Character, key: string): void {
        if(key === nameof(character, "currentWeapon")) {
            for (const [type, button] of this.weaponButtons) {
                button.selected = character.currentWeapon.value === type;
            }
            return;
        }
        if(key === nameof(character, "weapons")) {
            for (const [type, button] of this.weaponButtons) {
                button.count = character.weapons.get(type);
            }
            return;
        }
    }

    private updateHitArea(padding: number = 20): void {
        const bounds = this.getLocalBounds();
        this.hitArea = new Rectangle(bounds.x - padding, bounds.y - padding, bounds.width + padding * 2, bounds.height + padding * 2);
    }

    public override destroy(options?: IDestroyOptions | boolean): void {
        this.character.propertyChanged.remove(this.onCharacterPropertyChange, this);
        for (const [_type, button] of this.weaponButtons) {
            button.onUp.remove(this.onWeaponClickHandler, this);
        }
        super.destroy(options);
    }
}
